<template>
  <div class="ground" ref="groundRef" :active="active">
    <img class="image-mask" :src="image" />
    <img class="image" :src="image" />

    <div class="assets">
      <template v-for="(asset, i) in data.assets">
        <Asset
          :key="i"
          :data="asset"
        />
      </template>
    </div>

    <component
      v-if="data.hotspot"
      :is="data.url ? 'a' : 'div'"
      :href="data.url"
      class="hotspot"
      ref="hotspotRef"
      @mouseover="active = true"
      @mouseleave="active = false"
    >
      <div class="bubble">
        <div class="arrow" />
        <div class="title has-s-heading-size">{{ data.title }}</div>
        <div class="stars has-xs-body-size">
          <span>{{ stars }}/{{ maxStars }}</span>
          <StarIcon
            :class="[
              'star',
              'animate__animated',
              'animate__bounceIn'
            ]"
            :filled="stars > 0"
          />
        </div>
      </div>
    </component>
  </div>
</template>

<script>
import {computed, inject, onMounted, ref} from '@vue/composition-api'

import {toPixel} from '@/utils/positioning'
import StarIcon from '@/components/StarIcon'
import * as providers from '@world/providers'
import * as localStorage from '@/utils/local-storage'

export default {
  props: {
    data: {type: Object, required: true},
  },
  components: {
    StarIcon,
  },
  setup (props, context) {
    const {
      x,
      y,
      z,
      width,
      imageUrl,
      hotspot,
      games = [],
    } = props.data

    const maxStars = games.length * 3
    const stars = games.reduce((total, gameId) => (
      total + Number(localStorage.getGameItem(gameId, 'stars') || 0)
    ), 0)

    const groundRef = ref()
    const hotspotRef = ref()
    const active = ref(false)
    const options = inject(providers.OPTIONS)

    onMounted(() => {
      groundRef.value.style.top = toPixel(y)
      groundRef.value.style.left = toPixel(x)

      if (z !== undefined) {
        groundRef.value.style.zIndex = z
      }

      groundRef.value.style.width = toPixel(width)

      if (hotspot) {
        hotspotRef.value.style.top = toPixel(hotspot.y)
        hotspotRef.value.style.left = toPixel(hotspot.x)
        hotspotRef.value.style.width = toPixel(hotspot.width)
        hotspotRef.value.style.height = toPixel(hotspot.height)
      }
    })

    return {
      stars,
      maxStars,
      groundRef,
      hotspotRef,
      image: computed(() => {
        return `${options.assetBaseUrl}${imageUrl}`
      }),
      active,
    }
  },
}
</script>

<style scoped lang="scss">
.ground {
  position: absolute;
  z-index: 0;
  // Need to disable event's for all other than hotspot so that other elements won't overlap hotspots.
  pointer-events: none;

  .image {
    position: relative;
    width: 100%;
    z-index: 1;
    opacity: 1;
    transition: opacity 300ms ease;
  }

  .image-mask {
    filter: brightness(0) invert(1);
    position: absolute;
    width: 100%;
    z-index: 1;
  }
}

.assets {
  z-index: 2;
  position: absolute;
  top: 0;
  left: 0;
}

.hotspot {
  z-index: 100;
  position: absolute;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  pointer-events: all;
  text-decoration: none;

  .bubble {
    position: relative;
    top: -60px;
    background: linear-gradient(0deg, #DAECF5 0%, #FFA8C1 100%);
    padding: 20px 20px 10px 20px;
    border-radius: 15px 20px;
    box-shadow: 4px 4px 4px #2c2c2c34;
    font-size: 20px;
    text-transform: uppercase;
    font-weight: bold;
    color: #4C3D91;
    text-align: center;

    .stars {
      font-size: 16px;

      .star {
        margin-left: 0.3em;
        width: 0.8em;
        height: 0.8em;
      }
    }

    .arrow {
      position: absolute;
      width: 10px;
      height: 10px;
      transform: rotate(45deg);
      bottom: -5px;
      left: calc(50% - 5px);
      background: #DAECF5;
    }
  }
}

.ground[active=true] {
  .image {
    opacity: 0.5;
  }

  .bubble,
  .arrow {
    background: white;
  }
}
</style>
